import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import RegistrationForm from './components/RegistrationForm';
import ProgressIndicator from './components/ProgressIndicator';
import TrustSignals from './components/TrustSignals';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const Register = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const handleRegistrationSubmit = async (formData) => {
    setIsLoading(true);
    
    try {
      // Simulate API call for registration
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock successful registration
      console.log('Registration data:', formData);
      
      setShowSuccessMessage(true);
      
      // Redirect to document upload after showing success message
      setTimeout(() => {
        navigate('/document-upload', { 
          state: { 
            registrationComplete: true,
            userEmail: formData?.email,
            userName: `${formData?.firstName} ${formData?.lastName}`
          }
        });
      }, 2000);
      
    } catch (error) {
      console.error('Registration failed:', error);
      // Handle error (show error message, etc.)
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginRedirect = () => {
    navigate('/login');
  };

  if (showSuccessMessage) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)] px-4 py-8">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto">
              <Icon name="CheckCircle" size={32} className="text-success" />
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-foreground">Registration Successful!</h1>
              <p className="text-muted-foreground">
                Your account has been created successfully. You will be redirected to the document upload page to complete your verification.
              </p>
            </div>
            <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
              <Icon name="Clock" size={16} />
              <span>Redirecting in a few seconds...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Create Your Account
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Join MoneyTransfer Pro to send money internationally with competitive rates, 
            fast transfers, and complete regulatory compliance.
          </p>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Registration Form - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-card rounded-lg border border-border p-6 lg:p-8">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-foreground mb-2">
                  Personal Information
                </h2>
                <p className="text-sm text-muted-foreground">
                  Please provide accurate information as it will be used for identity verification 
                  and compliance with financial regulations.
                </p>
              </div>
              
              <RegistrationForm 
                onSubmit={handleRegistrationSubmit}
                isLoading={isLoading}
              />
            </div>

            {/* Already have account */}
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                Already have an account?{' '}
                <Button
                  variant="link"
                  onClick={handleLoginRedirect}
                  className="p-0 h-auto font-medium"
                >
                  Sign in here
                </Button>
              </p>
            </div>
          </div>

          {/* Sidebar - Progress and Trust Signals */}
          <div className="lg:col-span-1 space-y-6">
            {/* Progress Indicator */}
            <ProgressIndicator currentStep={1} totalSteps={4} />
            
            {/* Trust Signals */}
            <TrustSignals />
          </div>
        </div>

        {/* Bottom Notice */}
        <div className="mt-12 bg-muted/50 rounded-lg p-6 text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <Icon name="Shield" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Secure Registration Process</h3>
          </div>
          <p className="text-sm text-muted-foreground max-w-3xl mx-auto">
            Your registration initiates our comprehensive KYC (Know Your Customer) process, 
            which is required by financial regulations to ensure secure and compliant money transfers. 
            All information is encrypted and stored securely in compliance with GDPR and other data protection laws.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;